import { findByIdWithRelationsQuery } from './aircraft-model.graphql';
import { Injectable } from '@angular/core';
import { DocumentNode, FetchResult } from '@apollo/client/core';
import { DigitaiAircraftModel, DigitaiAircraftModelAircraftStatus, DigitaiAircraftStatus, DigitaiCreateAircraftModel, DigitaiUpdateAircraftModelById, DigitaiUpdateAircraftModels } from '@apps/digitai';
import { createMutation, deleteByIdMutation, deleteMutation, fields, findByIdQuery, findQuery, getQuery, insertMutation, paginationQuery, updateByIdMutation, updateMutation } from '@apps/digitai/aircraft-model';
import { AircraftModelAircraftStatusService } from '@apps/digitai/aircraft-model-aircraft-status';
import { AircraftStatusService } from '@apps/digitai/aircraft-status';
import { GraphQLHeaders, GraphQLService, GridData, parseGqlFields, QueryStatement } from '@aurora';
import { BehaviorSubject, first, map, Observable, tap } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class AircraftModelService
{
    paginationSubject$: BehaviorSubject<GridData<DigitaiAircraftModel> | null> = new BehaviorSubject(null);
    aircraftModelSubject$: BehaviorSubject<DigitaiAircraftModel | null> = new BehaviorSubject(null);
    aircraftModelsSubject$: BehaviorSubject<DigitaiAircraftModel[] | null> = new BehaviorSubject(null);

    // scoped subjects
    paginationScoped: { [key: string]: BehaviorSubject<GridData<DigitaiAircraftModel> | null>; } = {};
    aircraftModelScoped: { [key: string]: BehaviorSubject<DigitaiAircraftModel | null>; } = {};
    aircraftModelsScoped: { [key: string]: BehaviorSubject<DigitaiAircraftModel[] | null>; } = {};

    constructor(
        private readonly aircraftModelAircraftStatusService: AircraftModelAircraftStatusService,
        private readonly aircraftStatusService: AircraftStatusService,
        private readonly graphqlService: GraphQLService,
    ) {}

    /**
    * Getters
    */
    get pagination$(): Observable<GridData<DigitaiAircraftModel>>
    {
        return this.paginationSubject$.asObservable();
    }

    get aircraftModel$(): Observable<DigitaiAircraftModel>
    {
        return this.aircraftModelSubject$.asObservable();
    }

    get aircraftModels$(): Observable<DigitaiAircraftModel[]>
    {
        return this.aircraftModelsSubject$.asObservable();
    }

    // allows to store different types of pagination under different scopes this allows us
    // to have multiple observables with different streams of pagination data.
    setScopePagination(scope: string, pagination: GridData<DigitaiAircraftModel>): void
    {
        if (this.paginationScoped[scope])
        {
            this.paginationScoped[scope].next(pagination);
            return;
        }
        // create new subject if not exist
        this.paginationScoped[scope] = new BehaviorSubject(pagination);
    }

    // get pagination observable by scope
    getScopePagination(scope: string): Observable<GridData<DigitaiAircraftModel>>
    {
        if (this.paginationScoped[scope]) return this.paginationScoped[scope].asObservable();
        return null;
    }

    setScopeAircraftModel(scope: string, object: DigitaiAircraftModel): void
    {
        if (this.aircraftModelScoped[scope])
        {
            this.aircraftModelScoped[scope].next(object);
            return;
        }
        // create new subject if not exist
        this.aircraftModelScoped[scope] = new BehaviorSubject(object);
    }

    getScopeAircraftModel(scope: string): Observable<DigitaiAircraftModel>
    {
        if (this.aircraftModelScoped[scope]) return this.aircraftModelScoped[scope].asObservable();
        return null;
    }

    setScopeAircraftModels(scope: string, objects: DigitaiAircraftModel[]): void
    {
        if (this.aircraftModelsScoped[scope])
        {
            this.aircraftModelsScoped[scope].next(objects);
            return;
        }
        // create new subject if not exist
        this.aircraftModelsScoped[scope] = new BehaviorSubject(objects);
    }

    getScopeAircraftModels(scope: string): Observable<DigitaiAircraftModel[]>
    {
        if (this.aircraftModelsScoped[scope]) return this.aircraftModelsScoped[scope].asObservable();
        return null;
    }

    pagination(
        {
            graphqlStatement = paginationQuery,
            query = {},
            constraint = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<GridData<DigitaiAircraftModel>>
    {
        // get result, map ang throw data across observable
        return this.graphqlService
            .client()
            .watchQuery<{ pagination: GridData<DigitaiAircraftModel>; }>({
                query    : graphqlStatement,
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data.pagination),
                tap(pagination => scope ? this.setScopePagination(scope, pagination) : this.paginationSubject$.next(pagination)),
            );
    }

    findById(
        {
            graphqlStatement = findByIdQuery,
            id = null,
            constraint = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            id?: string;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<{
        object: DigitaiAircraftModel;
    }>
    {
        return this.graphqlService
            .client()
            .watchQuery<{
                object: DigitaiAircraftModel;
            }>({
                query    : parseGqlFields(graphqlStatement, fields, constraint),
                variables: {
                    id,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data),
                tap(data => scope ? this.setScopeAircraftModel(scope, data.object) : this.aircraftModelSubject$.next(data.object)),
            );
    }

    findByIdWithRelations(
        {
            graphqlStatement = findByIdWithRelationsQuery,
            id = '',
            constraint = {},
            queryPaginateAircraftModelsAircraftStatuses = {},
            constraintPaginateAircraftModelsAircraftStatuses = {},
            queryPaginateAircraftStatuses = {},
            constraintPaginateAircraftStatuses = {},
            queryGetAircraftModelsAircraftStatuses = {},
            constraintGetAircraftModelsAircraftStatuses = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            id?: string;
            constraint?: QueryStatement;
            queryPaginateAircraftModelsAircraftStatuses?: QueryStatement;
            constraintPaginateAircraftModelsAircraftStatuses?: QueryStatement;
            queryPaginateAircraftStatuses?: QueryStatement;
            constraintPaginateAircraftStatuses?: QueryStatement;
            queryGetAircraftModelsAircraftStatuses?: QueryStatement;
            constraintGetAircraftModelsAircraftStatuses?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<{
        object: DigitaiAircraftModel;
        digitaiPaginateAircraftModelsAircraftStatuses: GridData<DigitaiAircraftModelAircraftStatus>;
        digitaiPaginateAircraftStatuses: GridData<DigitaiAircraftStatus>;
        digitaiGetAircraftModelsAircraftStatuses: DigitaiAircraftModelAircraftStatus[];
    }>
    {
        return this.graphqlService
            .client()
            .watchQuery<{
                object: DigitaiAircraftModel;
                digitaiPaginateAircraftModelsAircraftStatuses: GridData<DigitaiAircraftModelAircraftStatus>;
                digitaiPaginateAircraftStatuses: GridData<DigitaiAircraftStatus>;
                digitaiGetAircraftModelsAircraftStatuses: DigitaiAircraftModelAircraftStatus[];
            }>({
                query    : parseGqlFields(graphqlStatement, fields, constraint),
                variables: {
                    id,
                    constraint,
                    queryPaginateAircraftModelsAircraftStatuses,
                    constraintPaginateAircraftModelsAircraftStatuses: {
                        ...constraintPaginateAircraftModelsAircraftStatuses,
                        where: {
                            aircraftModelId: id,
                        },
                        include: [
                            {
                                association: 'aircraftStatus',
                            },
                        ],
                    },
                    queryPaginateAircraftStatuses,
                    constraintPaginateAircraftStatuses,
                    queryGetAircraftModelsAircraftStatuses,
                    constraintGetAircraftModelsAircraftStatuses,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data),
                tap(data =>
                {
                    if (scope)
                    {
                        this.setScopeAircraftModel(scope, data.object);
                    }
                    else
                    {
                        this.aircraftModelSubject$.next(data.object);
                    }
                    this.aircraftModelAircraftStatusService.paginationSubject$.next(data.digitaiPaginateAircraftModelsAircraftStatuses);
                    this.aircraftStatusService.paginationSubject$.next(data.digitaiPaginateAircraftStatuses);
                    this.aircraftModelAircraftStatusService.aircraftModelsAircraftStatusesSubject$.next(data.digitaiGetAircraftModelsAircraftStatuses);
                }),
            );
    }

    find(
        {
            graphqlStatement = findQuery,
            query = {},
            constraint = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<{
        object: DigitaiAircraftModel;
    }>
    {
        return this.graphqlService
            .client()
            .watchQuery<{
                object: DigitaiAircraftModel;
            }>({
                query    : parseGqlFields(graphqlStatement, fields, query, constraint),
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data),
                tap(data => scope ? this.setScopeAircraftModel(scope, data.object) : this.aircraftModelSubject$.next(data.object)),
            );
    }

    get(
        {
            graphqlStatement = getQuery,
            query = {},
            constraint = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<{
        objects: DigitaiAircraftModel[];
    }>
    {
        return this.graphqlService
            .client()
            .watchQuery<{
                objects: DigitaiAircraftModel[];
            }>({
                query    : parseGqlFields(graphqlStatement, fields, query, constraint),
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data),
                tap(data => scope ? this.setScopeAircraftModels(scope, data.objects) : this.aircraftModelsSubject$.next(data.objects)),
            );
    }

    create<T>(
        {
            graphqlStatement = createMutation,
            object = null,
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            object?: DigitaiCreateAircraftModel;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    payload: object,
                },
                context: {
                    headers,
                },
            });
    }

    insert<T>(
        {
            graphqlStatement = insertMutation,
            objects = null,
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            objects?: DigitaiCreateAircraftModel[];
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    payload: objects,
                },
                context: {
                    headers,
                },
            });
    }

    updateById<T>(
        {
            graphqlStatement = updateByIdMutation,
            object = null,
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            object?: DigitaiUpdateAircraftModelById;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    payload: object,
                },
                context: {
                    headers,
                },
            });
    }

    update<T>(
        {
            graphqlStatement = updateMutation,
            object = null,
            query = {},
            constraint = {},
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            object?: DigitaiUpdateAircraftModels;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    payload: object,
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            });
    }

    deleteById<T>(
        {
            graphqlStatement = deleteByIdMutation,
            id = null,
            constraint = {},
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            id?: string;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    id,
                    constraint,
                },
                context: {
                    headers,
                },
            });
    }

    delete<T>(
        {
            graphqlStatement = deleteMutation,
            query = {},
            constraint = {},
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            });
    }
}
